import React, { useEffect, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Switch, Typography, useMediaQuery } from "@material-ui/core";
import { MoreVert, Replay, SaveAltOutlined } from "@material-ui/icons";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import calculateTimeAwaitingFlag from "../../utils/calculateTimeAwaitingFlag";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import QueueOverrideRequestAuthorizationModal from "../QueueOverrideRequestAuthorizationModal";
import ReopenTicketModal from "../ReopenTicketModal";
import SplitButton from "../SplitButton";
import TicketOptionsMenu from "../TicketOptionsMenu";
import toastError from "../../errors/toastError";
import validateLongAwaitingTimeTickets from "../../utils/validateLongAwaitingTimeTickets";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
	mergeMessagesToggleContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "wrap"
	},

	actionButtons: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		padding: "5px",
		flexWrap: "wrap",
		height: "75%",
		borderRadius:"20px",
		marginRight: 6,
		flex: "1",
		marginLeft: "auto",

		[theme.breakpoints.down("300")]: { flexDirection: "column", },
		"& > *": { margin: theme.spacing(1), },
	},

	actionButton: { "&:hover": {color: theme.palette.primary.main, }, },

	floatingButton: {
		transition: 'transform 0.3s',
		'&:hover': { transform: 'translateY(-5px)', },
	}
}));

const TicketActionButtons = ({ ticket, setTabOpen, messagesListDispatch, setShouldRefetchMessagesList, areMessagesBeingFetched }) => {
	//	***************
	//	** Variables **
	//	***************
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

	const timeoutImportMessagesRef = useRef(null);

	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [ticketsList, setTicketsList] = useState([]);

	const [openReopenTicketModal, setOpenReopenTicketModal] = useState(false);
	const [openQueueOverrideRequestAuthorizationModal, setOpenQueueOverrideRequestAuthorizationModal] = useState(false);

	const [mergeMessagesFromDifferentConnectionsSetting, setMergeMessagesFromDifferentConnectionsSetting] = useState(null);
	const [isMergeMessagesToggleChecked, setIsMergeMessagesToggleChecked] = useState(false);

	const {
		getSetting
		, isLoading
		, importMessages
		, getUserQueuesIds
		, queueOverrideAuthorizers
		, setAreThereQueueOverrideRequests
		, setShouldSyncRouteOnMenu
	} = useGlobalRegisters();

	// ***---- Flags ----***
	const showMergeMessagesFromDifferentConnections = mergeMessagesFromDifferentConnectionsSetting === "disabled"
		&& ticket.type === 0
		&& !["closed", "group"].includes(ticket.status);

	const showReopenTicket = !ticket.isGroup
		&& ticket.status === "closed"
		&& ![3, 4].includes(ticket.type);

	const showImportMessages = ticket.type === 0
		&& ["open", "group"].includes(ticket.status)
		&& !ticket.contact?.isMessagesImported;

	const showImportMessagesButtonOnTicketHeader = !isSmallScreen
		&& importMessages === "enabled";

	const showReturnTicket = !ticket.isGroup
		&& ticket.status === "open"
		&& ![3, 4].includes(ticket.type);

	const showCloseTicket = !ticket.isGroup
		&& ticket.status === "pending"
		&& user.profile === "admin";

	const showResolveTicket = !ticket.isGroup
		&& ticket.status === "open";

	const showAcceptTicket = !ticket.isGroup
		&& ticket.status === "pending"
		&& ![3, 4].includes(ticket.type);

	const showTicketOptions = (
		(ticket.isGroup && user.profile === "admin")
		|| ["open", "pending"].includes(ticket.status)
	);



	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		if (ticket.status !== "pending") return;

		setLoading(true);

		const delayDebounceFn = setTimeout(() => {
			const fetchPendingTickets = async () => {
				try {
					const userQueuesIds = await getUserQueuesIds(user.id);
					const { data: { tickets } } = await api.get("/tickets", { params: { status: "pending", queueIds: userQueuesIds }, });
					setTicketsList(tickets);
				}
				catch (exception) {
					console.log("Ticket Actions Buttons Use Effect 2 Exception:", exception);
					toastError(exception);
				}
				finally {
					setLoading(false);
				}
			};

			fetchPendingTickets();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticket.status, user.id]);

	useEffect(() => {
		if (isLoading) return;

		const mergeMessagesFromDifferentConnectionsSetting = getSetting("mergeMessagesFromDifferentConnectionsSetting");
		setMergeMessagesFromDifferentConnectionsSetting(mergeMessagesFromDifferentConnectionsSetting);
		if (mergeMessagesFromDifferentConnectionsSetting === "enabled") return;

		const mergeMessagesLocalStorage = localStorage.getItem("mergeMessagesFromDifferentConnections");
		if (!mergeMessagesLocalStorage) return;
		
		const shouldCheckMergeMessagesToggle = JSON
			.parse(mergeMessagesLocalStorage)
			.filter(element => element.ticketId === ticket.id && element.userId === user.id)
			.length > 0;

		setIsMergeMessagesToggleChecked(shouldCheckMergeMessagesToggle);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, ticket.id, user.id]);
	


	//	***************
	//	** Functions **
	//	***************
	const handleOpenReopenTicketModal = () => {
		setOpenReopenTicketModal(true);
	};

	const handleCloseReopenTicketModal = () => {
		setOpenReopenTicketModal(false);
	};

	const handleOpenQueueOverrideRequestAuthorizationModal = () => {
    setOpenQueueOverrideRequestAuthorizationModal(true);
  };

  const handleCloseQueueOverrideRequestAuthorizationModal = () => {
    setOpenQueueOverrideRequestAuthorizationModal(false);
  };

	const handleConfirmationOpen = () => {
		setConfirmationOpen(true);
	};

	const handleConfirmationClose = () => {
		setConfirmationOpen(false);
	};

	const handleImportMessages = async () => {
		try {
			const values = { whatsappId: ticket.whatsappId };
			const { data } = await api.put(`/contactImportMessages/${ticket.contactId}`, values);
			
			if (timeoutImportMessagesRef.current) clearTimeout(timeoutImportMessagesRef.current);

			if (!data.success) {
				toast.info(i18n.t(data.message));
				return;
			}

			toast.success(i18n.t("contactModal.importingMessagesBaileys"));
		}
		catch (exception) {
			toast.error(exception);
		}
	};

	const handleOpenTicketOptionsMenu = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseTicketOptionsMenu = (event) => {
		setAnchorEl(null);
	};

	const validateForceAcceptLongAwaitingTimeTickets = (ticket, ticketsList, considerOnlyWhatsappTickets) => {
		const awaitingTimeSetting = getSetting("awaitingTime");
		const forceAcceptLongAwaitingTimeTickets = getSetting("forceAcceptLongAwaitingTimeTickets");
		const timeAwaitingFlag = calculateTimeAwaitingFlag(ticket, parseInt(awaitingTimeSetting, 10));

		const shouldValidateLongAwaitingTimeTickets = forceAcceptLongAwaitingTimeTickets === "enabled"
			&& user.forceAcceptLongAwaitingTimeTickets
			&& !timeAwaitingFlag;

		return shouldValidateLongAwaitingTimeTickets
			? validateLongAwaitingTimeTickets(ticketsList, awaitingTimeSetting, considerOnlyWhatsappTickets)
			: false;
	};

	const validateForceAcceptFirstTicket = async (currentTicketId, currentTicketType, considerOnlyWhatsappTickets) => {
		try {
			const forceAcceptFirstTicketSetting = getSetting("forceAcceptFirstTicket");
			if (forceAcceptFirstTicketSetting === "disabled") return { canSkipTicket: true };

			const { data } = await api.post("/queueOverrides/validation", {
				currentTicketId
				, currentTicketType
				, shouldConsiderOnlyWhatsappTickets: considerOnlyWhatsappTickets
				, queueOverrideAuthorizers
			});

			return data;
		}
		catch (exception) {
			console.log("validateForceAcceptFirstTicket exception:", exception);
			toast.error(exception);
			return { canSkipTicket: false, message: "backendErrors.ERR_FORCE_ACCEPT_FIRST_TICKET", shouldQueueOverrideBeAuthorized: false };
		}
  };

	const handleUpdateTicketStatus = async (status, userId) => {
		setLoading(true);

		if (status === "open") {
			// ***---- Validation: Force Accept Long Awaiting Time Tickets ----***
			const areThereLongAwaitingTimeTickets = validateForceAcceptLongAwaitingTimeTickets(ticket, ticketsList, true);

			if (areThereLongAwaitingTimeTickets) {
				toast.info(i18n.t("backendErrors.ERR_FORCE_ACCEPT_LONG_AWAITING_TIME_TICKETS"));
				setLoading(false);
        return;
			}

			// ***---- Validation: Force Accept First Ticket ----***
			const { canSkipTicket, message, shouldQueueOverrideBeAuthorized } = await validateForceAcceptFirstTicket(
				ticket.id
				, ticket.type
				, true
			);
			
			if (!canSkipTicket) {
				toast.info(i18n.t(message));

				if (shouldQueueOverrideBeAuthorized) handleOpenQueueOverrideRequestAuthorizationModal();
				setLoading(false);
				return;
			}

			// ***---- Processing: Update Ticket ----***
			try {
				await api.put(`/tickets/${ticket.id}/1`, {
					status: status,
					userId: userId,
					directClosedById: null,
					isReturned: false,
					isTransfered: ticket.isTransfered,
					isBotInteractionFinished: true,
					updateLastActivityAt: true,
					contactId: ticket.contactId
				});
	
				DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticket.id}`);
				setShouldSyncRouteOnMenu(true);

				setTabOpen("open");
				localStorage.setItem("tabOpenTickets", "open");
			}
			catch (exception) {
				console.log("Handle Update Ticket Status Exception:", exception);
				toastError(exception);
			}
			finally {
				setLoading(false);
				api.get("/queueOverrides/exists").then(response => setAreThereQueueOverrideRequests(response.data));
			}
		}
		else {
			try {
				const isTicketBeingPending = status === "pending";
				const isTicketBeingClosed = status === "closed";

				await api.put(`/tickets/${ticket.id}/0`, {
					status,
					userId: userId && !isTicketBeingClosed ? userId : null,
					directClosedById: userId && isTicketBeingClosed ? userId : null,
					isReturned: isTicketBeingPending,
					isTransfered: !isTicketBeingPending ? ticket.isTransfered : false,
					isBotInteractionFinished: true,
					updateLastActivityAt: isTicketBeingPending,
					contactId: ticket.contactId
				});

				if (isTicketBeingPending) {
					setTabOpen(status);
					localStorage.setItem("tabOpenTickets", status);
				}
				
				DefaultFunctionsUtils.redirectToTicketsPage(history);
				setShouldSyncRouteOnMenu(true);
			}
			catch (exception) {
				console.log("Handle Update Ticket Status Exception:", exception);
				toastError(exception);
			}
			finally {
				setLoading(false);
				api.get("/queueOverrides/exists").then(response => setAreThereQueueOverrideRequests(response.data));
			}
		}
	};

	const handleChangeMergeMessagesToggle = () => {
		if (areMessagesBeingFetched) return;

		const shouldCheckToggle = !isMergeMessagesToggleChecked;
		setIsMergeMessagesToggleChecked(shouldCheckToggle);

		const mergeMessagesLocalStorage = localStorage.getItem("mergeMessagesFromDifferentConnections");
		const mergeMessagesArray = mergeMessagesLocalStorage ? JSON.parse(mergeMessagesLocalStorage) : [];

		if (shouldCheckToggle) {
			const elementExists = mergeMessagesArray.some(element => element.ticketId === ticket.id && element.userId === user.id);
			if (!elementExists) mergeMessagesArray.push({ ticketId: ticket.id, userId: user.id });
		}
		else {
			const elementIndex = mergeMessagesArray.findIndex(element => element.ticketId === ticket.id && element.userId === user.id);
			if (elementIndex !== -1) mergeMessagesArray.splice(elementIndex, 1);
		}

		localStorage.setItem("mergeMessagesFromDifferentConnections", JSON.stringify(mergeMessagesArray));
		setShouldRefetchMessagesList(true);
	};



	//	************
	//	** Return **
	//	************
	return (
		<>
			<ReopenTicketModal
				modalOpen={openReopenTicketModal}
				onClose={handleCloseReopenTicketModal}
				setTabOpen={setTabOpen}
				ticket={ticket}
			/>

			{ticket.type === 0 && (
				<QueueOverrideRequestAuthorizationModal
					open={openQueueOverrideRequestAuthorizationModal}
					onClose={handleCloseQueueOverrideRequestAuthorizationModal}
					ticketId={ticket.id}
					contactId={ticket.contactId}
				/>
			)}

			<div className={classes.actionButtons}>
				{/* ***---- Merge Messages From Different Connections ----*** */}
				{showMergeMessagesFromDifferentConnections && (
					<div
						style={{ cursor: areMessagesBeingFetched ? "default" : "pointer" }}
						className={classes.mergeMessagesToggleContainer}
						onClick={handleChangeMergeMessagesToggle}
					>
						<Switch checked={isMergeMessagesToggleChecked} disabled={areMessagesBeingFetched} />
						<Typography>{i18n.t("messagesList.header.buttons.mergeMessagesFromDifferentConnectionsSetting")}</Typography>
					</div>
				)}

				{/* ***---- Reopen Ticket ----*** */}
				{showReopenTicket && (
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={handleOpenReopenTicketModal}
						className={classes.floatingButton}
					>
						{i18n.t("messagesList.header.buttons.reopen")}
					</ButtonWithSpinner>
				)}

				{/* ***---- Import Messages ----*** */}
				{showImportMessages && (
					<>
						<ConfirmationModal
							title={i18n.t("contactModal.confirmationModal.titleBaileys")}
							open={confirmationOpen}
							onClose={handleConfirmationClose}
							onConfirm={async () => await handleImportMessages()}
						>
							{i18n.t("contactModal.confirmationModal.messageBaileys")}
						</ConfirmationModal>

						{showImportMessagesButtonOnTicketHeader && (
							<ButtonWithSpinner
								loading={loading}
								startIcon={<SaveAltOutlined />}
								size="small"
								onClick={handleConfirmationOpen}
								className={classes.floatingButton}
							>
								{i18n.t("contactModal.buttons.importMessages")}
							</ButtonWithSpinner>
						)}
					</>
				)}

				{/* ***---- Return Ticket ----*** */}
				{showReturnTicket && (
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={async () => await handleUpdateTicketStatus("pending", null)}
						className={classes.floatingButton}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
				)}

				{/* ***---- Close Ticket ----*** */}
				{showCloseTicket && (
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={async () => await handleUpdateTicketStatus("closed", user?.id)}
						className={classes.floatingButton}
					>
						{i18n.t("messagesList.header.buttons.close")}
					</ButtonWithSpinner>
				)}

				{/* ***---- Resolve Ticket ----*** */}
				{showResolveTicket && (
					<SplitButton ticket={ticket} />
				)}

				{/* ***---- Accept Ticket ----*** */}
				{showAcceptTicket && (
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={async () => await handleUpdateTicketStatus("open", user?.id)}
						className={classes.floatingButton}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
				)}

				{/* ***---- Ticket Options ----*** */}
				{showTicketOptions && (
					<>
						<IconButton color="inherit" onClick={handleOpenTicketOptionsMenu} className={classes.actionButton}>
							<MoreVert />
						</IconButton>
						
						<TicketOptionsMenu
							ticket={ticket}
							anchorEl={anchorEl}
							menuOpen={ticketOptionsMenuOpen}
							handleClose={handleCloseTicketOptionsMenu}
							messagesListDispatch={messagesListDispatch}
						/>
					</>
				)}
			</div>
		</>
	);
};

export default TicketActionButtons;
